import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from '@/context/auth-context';
import { Navigate } from 'react-router-dom';
import {
  Users,
  Globe,
  FileText,
  Database,
  Activity,
  MessageSquare,
  Clock,
  TrendingUp,
  Server,
  BarChart3,
  Image,
  Layers,
  Zap,
  AlertCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { API_URL } from '@/config/api';

interface DashboardMetrics {
  // Basic counts
  totalSites: number;
  totalUsers: number;
  totalPages: number;
  totalSectors: number;
  sectorsWithTopics: number;
  cachedImages: number;
  activeSessions: number;
  recordedExperiences: number;
  storedImages: number;
  totalChatLogs: number;
  chatLogsLast24h: number;
  historicalSessions: number;
  
  // Additional metrics
  activeUsersLast7Days: number;
  activeUsersLast30Days: number;
  avgSessionDuration: number;
  totalAgentRequests: number;
  errorRate: number;
  
  // Time series data
  dailyStats: Array<{
    date: string;
    users: number;
    pages: number;
    chats: number;
    sessions: number;
  }>;
  
  // Agent activity
  agentActivity: Array<{
    agent: string;
    requests: number;
    successRate: number;
  }>;
  
  // Top sites
  topSites: Array<{
    name: string;
    url: string;
    pageViews: number;
    chatInteractions: number;
  }>;
  
  // Sector distribution
  sectorDistribution: Array<{
    name: string;
    value: number;
  }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const MetricCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  trend?: number;
  loading?: boolean;
}> = ({ title, value, icon, description, trend, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-2" />
          {description && <Skeleton className="h-4 w-32" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {trend !== undefined && (
          <div className="flex items-center mt-2">
            <TrendingUp className={`h-4 w-4 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={`text-xs ml-1 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const { selectedSite } = useSite();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('7d');

  // Check if user is super admin
  if (!user || user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    fetchDashboardMetrics();
  }, [timeRange]);
  
  // Track "visit_admin" onboarding step when component mounts
  useEffect(() => {
    if (selectedSite) {
      authFetch('/onboarding/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite._id || selectedSite.id,
          activity: {
            type: 'page_visit',
            path: '/admin',
            timestamp: new Date().toISOString()
          }
        })
      }).catch(err => console.error('Failed to track admin visit:', err));
    }
  }, [selectedSite]);

  const fetchDashboardMetrics = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`/admin/dashboard-metrics?timeRange=${timeRange}`);
      const data = await handleResponse<DashboardMetrics>(response);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error Loading Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Super Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          System overview and analytics
        </p>
      </div>

      {/* Time Range Selector */}
      <div className="flex justify-end">
        <Tabs value={timeRange} onValueChange={setTimeRange}>
          <TabsList>
            <TabsTrigger value="24h">24 Hours</TabsTrigger>
            <TabsTrigger value="7d">7 Days</TabsTrigger>
            <TabsTrigger value="30d">30 Days</TabsTrigger>
            <TabsTrigger value="90d">90 Days</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Sites"
          value={metrics?.totalSites || 0}
          icon={<Globe className="h-4 w-4 text-muted-foreground" />}
          description="Active websites"
          loading={loading}
        />
        <MetricCard
          title="Total Users"
          value={metrics?.totalUsers || 0}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          description={`${metrics?.activeUsersLast7Days || 0} active this week`}
          loading={loading}
        />
        <MetricCard
          title="Total Pages"
          value={metrics?.totalPages || 0}
          icon={<FileText className="h-4 w-4 text-muted-foreground" />}
          description="Indexed pages"
          loading={loading}
        />
        <MetricCard
          title="Active Sessions"
          value={metrics?.activeSessions || 0}
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
          description="Current active users"
          loading={loading}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <MetricCard
          title="Chat Logs (24h)"
          value={metrics?.chatLogsLast24h || 0}
          icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
          loading={loading}
        />
        <MetricCard
          title="Total Chats"
          value={metrics?.totalChatLogs || 0}
          icon={<MessageSquare className="h-4 w-4 text-muted-foreground" />}
          loading={loading}
        />
        <MetricCard
          title="Sectors"
          value={metrics?.totalSectors || 0}
          icon={<Layers className="h-4 w-4 text-muted-foreground" />}
          description={`${metrics?.sectorsWithTopics || 0} with topics`}
          loading={loading}
        />
        <MetricCard
          title="Cached Images"
          value={metrics?.cachedImages || 0}
          icon={<Image className="h-4 w-4 text-muted-foreground" />}
          loading={loading}
        />
        <MetricCard
          title="Agent Requests"
          value={metrics?.totalAgentRequests || 0}
          icon={<Zap className="h-4 w-4 text-muted-foreground" />}
          loading={loading}
        />
        <MetricCard
          title="Avg Session"
          value={metrics ? `${Math.round(metrics.avgSessionDuration / 60)}m` : '0m'}
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          loading={loading}
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Activity Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Over Time</CardTitle>
            <CardDescription>Daily activity metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics?.dailyStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="users" 
                      stroke="#8884d8" 
                      name="Users"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="chats" 
                      stroke="#82ca9d" 
                      name="Chats"
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke="#ffc658" 
                      name="Sessions"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sector Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Sector Distribution</CardTitle>
            <CardDescription>Sites by sector</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={metrics?.sectorDistribution || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {metrics?.sectorDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Activity</CardTitle>
          <CardDescription>Requests and success rates by agent</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {loading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics?.agentActivity || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="agent" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="requests" fill="#8884d8" name="Requests" />
                  <Bar dataKey="successRate" fill="#82ca9d" name="Success Rate %" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Sites Table */}
      <Card>
        <CardHeader>
          <CardTitle>Top Sites</CardTitle>
          <CardDescription>Most active sites by page views and interactions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-gray-50">
                  <tr>
                    <th className="px-6 py-3">Site Name</th>
                    <th className="px-6 py-3">URL</th>
                    <th className="px-6 py-3">Page Views</th>
                    <th className="px-6 py-3">Chat Interactions</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics?.topSites.map((site, index) => (
                    <tr key={index} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{site.name}</td>
                      <td className="px-6 py-4">
                        <a href={site.url} target="_blank" rel="noopener noreferrer" 
                           className="text-blue-600 hover:underline">
                          {site.url}
                        </a>
                      </td>
                      <td className="px-6 py-4">{site.pageViews.toLocaleString()}</td>
                      <td className="px-6 py-4">{site.chatInteractions.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle>System Health</CardTitle>
          <CardDescription>Current system status and performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="flex items-center space-x-2">
              <Server className="h-4 w-4 text-green-500" />
              <span className="text-sm">All services operational</span>
            </div>
            <div className="flex items-center space-x-2">
              <Database className="h-4 w-4 text-green-500" />
              <span className="text-sm">Database healthy</span>
            </div>
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-green-500" />
              <span className="text-sm">Error rate: {metrics?.errorRate || 0}%</span>
            </div>
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4 text-green-500" />
              <span className="text-sm">Avg response: 245ms</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}